import { useState, type FormEvent } from 'react'
import { Loader2, User as UserIcon, Building2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'
import ClientRatesPanel from '../rates/ClientRatesPanel'
import IssuerSelect from '../billing/IssuerSelect'
import { useAuth } from '../../contexts/AuthContext'
import type { ClientDetail, CreateClientInput } from '@toga/shared'

interface ClientFormProps {
  initial: ClientDetail | null
  /** Valores con los que arranca un cliente NUEVO (p. ej. el remitente de un correo). */
  prefill?: Partial<CreateClientInput>
  onClose: () => void
  /** Recibe el id cuando se acaba de crear un cliente. */
  onSaved: (createdClientId?: string) => void
}

function emptyForm(): CreateClientInput {
  return {
    type: 'PERSONA',
    firstName: '',
    lastName1: '',
    lastName2: '',
    legalName: '',
    nationalId: '',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
    country: 'ES',
    notes: '',
    issuerId: null,
  }
}

export default function ClientFormModal({ initial, prefill, onClose, onSaved }: ClientFormProps) {
  const auth = useAuth()
  const canWrite = auth.status === 'authenticated' && auth.user.role !== 'LECTURA'
  const canSetIssuer =
    auth.status === 'authenticated' && (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')
  const isEdit = initial !== null
  const [form, setForm] = useState<CreateClientInput>(
    initial
      ? {
          type: initial.type,
          firstName: initial.firstName ?? '',
          lastName1: initial.lastName1 ?? '',
          lastName2: initial.lastName2 ?? '',
          legalName: initial.legalName ?? '',
          nationalId: initial.nationalId ?? '',
          email: initial.email ?? '',
          phone: initial.phone ?? '',
          mobile: initial.mobile ?? '',
          address: initial.address ?? '',
          city: initial.city ?? '',
          postalCode: initial.postalCode ?? '',
          province: initial.province ?? '',
          country: initial.country,
          notes: initial.notes ?? '',
          issuerId: initial.issuerId,
        }
      : { ...emptyForm(), ...prefill }
  )
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const upd = <K extends keyof CreateClientInput>(k: K, v: CreateClientInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      // Convertimos strings vacíos a null para que pasen las validaciones .nullable()
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
      ) as unknown as CreateClientInput
      payload.type = form.type
      payload.country = form.country || 'ES'

      if (isEdit && initial) {
        await api.patch(`/clients/${initial.id}`, payload)
        onSaved()
      } else {
        const created = await api.post<{ client: { id: string } }>('/clients', payload)
        onSaved(created.client.id)
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as
          | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
          | undefined
        const fieldErr = body?.issues?.fieldErrors
        const firstFieldErr = fieldErr ? Object.values(fieldErr).flat().find(Boolean) : undefined
        setError(firstFieldErr ?? body?.error ?? 'No se pudo guardar.')
      } else {
        setError('Error de red.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={isEdit ? 'Editar cliente' : 'Nuevo cliente'}
      maxWidth="max-w-2xl"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => upd('type', 'PERSONA')}
            className={[
              'flex-1 px-3 py-2 rounded-lg text-sm border flex items-center justify-center gap-2 transition',
              form.type === 'PERSONA'
                ? 'bg-purple-500/15 border-purple-500/40 text-purple-200'
                : 'bg-slate-900/40 border-slate-700/50 t-secondary hover:bg-slate-900/60',
            ].join(' ')}
            disabled={isEdit}
          >
            <UserIcon size={14} /> Persona física
          </button>
          <button
            type="button"
            onClick={() => upd('type', 'EMPRESA')}
            className={[
              'flex-1 px-3 py-2 rounded-lg text-sm border flex items-center justify-center gap-2 transition',
              form.type === 'EMPRESA'
                ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-200'
                : 'bg-slate-900/40 border-slate-700/50 t-secondary hover:bg-slate-900/60',
            ].join(' ')}
            disabled={isEdit}
          >
            <Building2 size={14} /> Persona jurídica
          </button>
        </div>

        {form.type === 'PERSONA' ? (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs t-muted mb-1.5">Nombre</label>
              <input
                required
                value={form.firstName ?? ''}
                onChange={(e) => upd('firstName', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs t-muted mb-1.5">Primer apellido</label>
              <input
                required
                value={form.lastName1 ?? ''}
                onChange={(e) => upd('lastName1', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs t-muted mb-1.5">Segundo apellido</label>
              <input
                value={form.lastName2 ?? ''}
                onChange={(e) => upd('lastName2', e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-xs t-muted mb-1.5">Razón social</label>
            <input
              required
              value={form.legalName ?? ''}
              onChange={(e) => upd('legalName', e.target.value)}
              className={inputCls}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1.5">NIF / DNI / NIE / CIF</label>
            <input
              value={form.nationalId ?? ''}
              onChange={(e) => upd('nationalId', e.target.value.toUpperCase())}
              placeholder="12345678Z"
              className={`${inputCls} font-mono`}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">Email</label>
            <input
              type="email"
              value={form.email ?? ''}
              onChange={(e) => upd('email', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">Teléfono</label>
            <input
              value={form.phone ?? ''}
              onChange={(e) => upd('phone', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">Móvil</label>
            <input
              value={form.mobile ?? ''}
              onChange={(e) => upd('mobile', e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">Dirección</label>
          <input
            value={form.address ?? ''}
            onChange={(e) => upd('address', e.target.value)}
            className={inputCls}
          />
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="col-span-2">
            <label className="block text-xs t-muted mb-1.5">Ciudad</label>
            <input
              value={form.city ?? ''}
              onChange={(e) => upd('city', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">CP</label>
            <input
              value={form.postalCode ?? ''}
              onChange={(e) => upd('postalCode', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">Provincia</label>
            <input
              value={form.province ?? ''}
              onChange={(e) => upd('province', e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        {canSetIssuer && (
          <div>
            <label className="block text-xs t-muted mb-1.5">Facturar con (emisor)</label>
            <IssuerSelect
              value={form.issuerId}
              onChange={(id) => upd('issuerId', id)}
              emptyLabel="El que corresponda (expediente, abogado o bufete)"
              className={inputCls}
            />
            <p className="t-faint text-[11px] mt-1">
              Solo si a este cliente se le factura siempre con el mismo NIF. El del expediente manda
              sobre este.
            </p>
          </div>
        )}

        <div>
          <label className="block text-xs t-muted mb-1.5">Notas</label>
          <textarea
            value={form.notes ?? ''}
            onChange={(e) => upd('notes', e.target.value)}
            rows={3}
            className={inputCls}
          />
        </div>

        {isEdit && initial && <ClientRatesPanel clientId={initial.id} canWrite={canWrite} />}

        {error && (
          <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-slate-800/60 hover:bg-slate-800 t-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Guardar cambios' : 'Crear cliente'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
